import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Checkbox } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'
import DragIndicator from '@material-ui/icons/DragIndicator'
import clsx from 'clsx'
import React from 'react'
import CustomHoverTooltip from '../CustomHoverTooltip'
import { sharedTableStyles } from '../Spectable.styles'
import { droppableContainerStyles } from './DroppableContainer'

const draggableRowStyles = makeStyles({
    items: {
        padding: '8px 4px 8px 8px',
        borderBottom: '1px solid #c4c4c4',
    },
    draggingItem: {
        backgroundColor: '#fff',
        border: '1px solid #c4c4c4',
    },
    pinPadding: {
        padding: 5,
    },
})

export interface DraggableRowProps {
    id?: string
    displayName?: string
    droppableId: string
    index: number
    orderPrefix?: string
    pinActive?: boolean
    showPinComponent?: boolean
    collapseColumnChecked?: boolean
    showCollapseToggle?: boolean
    handlePinClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    handleRemoveClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    handleCollapseCheck?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => void
}

const DraggableRow = ({
    displayName,
    droppableId,
    id,
    index,
    orderPrefix = '',
    pinActive,
    showPinComponent,
    collapseColumnChecked,
    showCollapseToggle,
    handlePinClick,
    handleRemoveClick,
    handleCollapseCheck,
}: DraggableRowProps) => {
    const draggableClasses = draggableRowStyles()
    const droppableClasses = droppableContainerStyles()
    const sharedTableClasses = sharedTableStyles()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: id! })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }
    return (
        <div ref={setNodeRef} data-testid="draggable" style={style}>
            {displayName !== '' && displayName && (
                <GridContainer
                    alignItems="center"
                    wrap="nowrap"
                    className={clsx(draggableClasses.items, 'draggable-row')}
                >
                    <GridItem {...attributes} {...listeners}>
                        <DragIndicator />
                    </GridItem>
                    <GridItem
                        xs={showCollapseToggle ? 4 : 7}
                        className={clsx(
                            sharedTableClasses.textOverflowEllipsis,
                            'row-name-container'
                        )}
                    >
                        <CustomHoverTooltip text={displayName ?? ''} />
                    </GridItem>
                    <GridItem xs={2} className={droppableClasses.centerText}>
                        {showPinComponent && (
                            <IconButton
                                size="small"
                                onClick={handlePinClick}
                                data-droppable-id={droppableId}
                                data-item-index={index}
                                className="pin-button"
                            >
                                <img
                                    src={`https://avitrucdn.azureedge.net/knowledgepoint/client/pin_${
                                        pinActive ? '' : 'in'
                                    }active.png`}
                                    alt="active or inactive pin"
                                    className={draggableClasses.pinPadding}
                                />
                            </IconButton>
                        )}
                    </GridItem>
                    <GridItem
                        xs={2}
                        className={clsx(
                            droppableClasses.centerText,
                            'row-order-container'
                        )}
                    >
                        {`${orderPrefix}${index + 1}`}
                    </GridItem>
                    <GridItem xs={2} className={droppableClasses.centerText}>
                        <IconButton
                            onClick={handleRemoveClick}
                            size="small"
                            data-droppable-id={droppableId}
                            data-remove-id={index}
                            className="remove-button"
                            aria-label="Remove"
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </GridItem>
                    {showCollapseToggle && (
                        <GridItem
                            xs={2}
                            className={droppableClasses.centerText}
                        >
                            <Checkbox
                                name={id}
                                color="primary"
                                inputProps={{
                                    'aria-label': 'collapse',
                                }}
                                checked={collapseColumnChecked}
                                className="collapse-checkbox"
                                onChange={handleCollapseCheck}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            )}
        </div>
    )
}

export default DraggableRow
